import { useMemo } from "react";
import tinycolor from "tinycolor2";

/**
 * Type definition for the useAccessibleColor hook
 * @param hex - The hex color to generate accessible colors for
 * @param mode - The color mode (light or dark)
 * @returns Object containing accessible foreground, background, and border colors
 */
export type UseAccessibleColorType = (
  hex?: string,
  mode?: "light" | "dark",
) => { foreground?: string; background?: string; border?: string };

/**
 * Custom hook for generating accessible color combinations that meet WCAG contrast requirements.
 *
 * Features:
 * - Generates accessible foreground, background, and border colors
 * - Supports both light and dark color modes
 * - Ensures WCAG contrast ratio compliance (4.5:1 for text, 3:1 for borders)
 * - Automatically adjusts colors to meet accessibility standards
 * - Handles invalid color inputs gracefully
 * - Uses tinycolor2 for color manipulation and contrast calculations
 * - Optimized with useMemo for performance
 * - Implements intelligent color selection algorithms
 * - Provides fallback strategies for edge cases
 * - Supports dynamic theme switching
 *
 * Algorithm Details:
 * - Light Mode: Selects white or dark text based on background contrast
 * - Dark Mode: Adjusts both foreground and background for optimal contrast
 * - Border Generation: Ensures 3:1 contrast ratio with page background
 * - Iterative Adjustment: Uses up to 16 iterations for color optimization
 * - Luminance Thresholds: Maintains appropriate darkness levels for dark themes
 *
 * @param hex - The hex color to generate accessible colors for (e.g., "#FF0000")
 * @param mode - The color mode ("light" or "dark") for context-aware color generation
 * @returns Object containing accessible foreground, background, and border colors in hex format
 */
export const useAccessibleColor: UseAccessibleColorType = (hex, mode) => {
  return useMemo(() => {
    if (!hex)
      return {
        foreground: undefined,
        background: undefined,
        border: undefined,
      };
    if (!tinycolor(hex).isValid()) throw new Error("color is not valid.");

    return mode === "dark"
      ? getAccessibleDarkColor(hex, mode)
      : getAccessibleLightColor(hex, mode);
  }, [hex, mode]);
};

const lightColor = tinycolor("#FFFFFF");
const darkColor = tinycolor("#141414");
const black = tinycolor("#000000");

/**
 * Generates accessible colors for light mode themes.
 *
 * Algorithm:
 * 1. Determines if background is light or dark using contrast comparison
 * 2. Selects appropriate foreground color (white or dark)
 * 3. Ensures 4.5:1 contrast ratio for text readability
 * 4. Falls back to black text if contrast is insufficient
 * 5. Generates border color with 3:1 contrast against page background
 * 6. Iteratively adjusts border color until contrast requirements are met
 *
 * @param hex - The hex color to generate accessible colors for
 * @param mode - The color mode (light or dark)
 * @returns Object containing accessible foreground, background, and border colors
 */
const getAccessibleLightColor: UseAccessibleColorType = (hex, mode) => {
  const bgColor = tinycolor(hex);
  const isBgColorDark =
    tinycolor.readability(bgColor, lightColor) >
    tinycolor.readability(bgColor, darkColor);
  let fgColor = isBgColorDark ? lightColor : darkColor;

  // Contrast ratio between fgcolor and bgcolor should be >= 4.5
  let contrast = tinycolor.readability(fgColor, bgColor);

  // Special case: If we hit a middle color that isn't 4.5 against either our
  // light or dark foreground colors, use #000000 as our foreground
  if (contrast < 4.5) {
    fgColor = black;
  }

  const borderColor = bgColor.clone();
  const pageColor = mode === "dark" ? darkColor : lightColor;

  // Contrast ratio between border and background should be >= 3
  contrast = tinycolor.readability(borderColor, pageColor);

  // limit to 16 iterations
  for (let i = 0; i < 16; i++) {
    // if contrast ratios are good, then kick out of the loop
    if (contrast >= 3) break;

    if (mode === "dark") {
      borderColor.lighten(2.5);
    } else {
      borderColor.darken(2.5);
    }

    contrast = tinycolor.readability(borderColor, pageColor);
  }

  return {
    foreground: fgColor.toHexString(),
    background: bgColor.toHexString(),
    border: borderColor.toHexString(),
  };
};

const luminanceThresholdDark = darkColor
  .clone()
  .lighten()
  .lighten()
  .getLuminance();

/**
 * Generates accessible colors for dark mode themes.
 *
 * Algorithm:
 * 1. Starts with the provided color for both foreground and background
 * 2. Ensures background meets darkness threshold for dark themes
 * 3. Iteratively adjusts foreground and background for 4.5:1 contrast
 * 4. Prioritizes background darkening to maintain theme consistency
 * 5. Lightens foreground only when background is sufficiently dark
 * 6. Generates border color with 3:1 contrast against page background
 * 7. Uses luminance thresholds to maintain appropriate darkness levels
 *
 * @param hex - The hex color to generate accessible colors for
 * @param mode - The color mode (light or dark)
 * @returns Object containing accessible foreground, background, and border colors
 */
const getAccessibleDarkColor: UseAccessibleColorType = (hex, mode) => {
  const bgColor = tinycolor(hex);
  const fgColor = tinycolor(hex);

  // Contrast ratio between fgcolor and bgcolor should be >= 4.5
  let contrast = tinycolor.readability(fgColor, bgColor);

  // limit to 16 iterations
  for (let i = 0; i < 16; i++) {
    // if contrast ratios are good, then kick out of the loop
    if (contrast >= 4.5 && bgColor.getLuminance() <= luminanceThresholdDark)
      break;

    // keep darkening the background until it's darker than the threshold
    if (bgColor.getLuminance() > luminanceThresholdDark) {
      bgColor.darken();

      // after that, do the contrast loop
    } else if (fgColor.getLuminance() < 1) {
      fgColor.lighten();
    } else {
      bgColor.darken();
    }

    contrast = tinycolor.readability(fgColor, bgColor);
  }

  const borderColor = bgColor.clone();
  const pageColor = mode === "dark" ? darkColor : lightColor;

  // Contrast ratio between border and background should be >= 3
  contrast = tinycolor.readability(borderColor, pageColor);

  // limit to 16 iterations
  for (let i = 0; i < 16; i++) {
    // if contrast ratios are good, then kick out of the loop
    if (contrast >= 3) break;

    if (mode === "dark") {
      borderColor.lighten(2.5);
    } else {
      borderColor.darken(2.5);
    }

    contrast = tinycolor.readability(borderColor, pageColor);
  }

  return {
    foreground: fgColor.toHexString(),
    background: bgColor.toHexString(),
    border: borderColor.toHexString(),
  };
};
